import type { CidianSuggestion } from "../content/cidian/cidian-suggestion-line.js";
import {
  type CidianSyllable,
  untoned,
} from "../content/cidian/cidian-syllables.js";

/**
 * One row of the suggestion list, as a reader reads it and a pointer hits it.
 *
 * The word at the size the archive draws a headword, and the reading beside it
 * muted and smaller, which is the shape `layouts/cidian/summary.html` gives a
 * result. `modules/search.scss` draws both.
 *
 * **The reading is coloured by tone**, one span per syllable, in the classes
 * `@kensio/pinyinjs` writes and `components/hanzi.scss` paints. That is how
 * every other reading on this site is drawn, the archive squares included, and
 * a suggestion list is the one place a reader met a grey one. The tones come
 * off the published index, which holds `{4rèn}{5shi}` for exactly this: see
 * `src/content/cidian/cidian-syllables.ts`. A syllable the section gave
 * no tone takes the span without a tone class, which is what the theme writes
 * for one.
 *
 * No `lang` on either span, for the reason the box itself carries none.
 * `$body-font` falls through to the Chinese face for the characters Baloo 2
 * cannot draw, and the reading is Latin letters either way.
 *
 * `data-cb-word` is what a click reads. The row is not a link, a suggestion
 * putting a word in the box and submitting the form instead of going anywhere
 * of its own, so there is no `href` to take the word off.
 */

/**
 * The word a row stands for, or nothing where the target is not in a row.
 *
 * Read back off the same attribute this file writes, so a press anywhere
 * inside a row (on the word, on the reading, on the padding) answers with the
 * row's word. `closest` is what makes the two spans hits rather than misses.
 */
export function wordAt(target: EventTarget | null): string | undefined {
  return target instanceof Element
    ? (target.closest<HTMLElement>("[data-cb-word]")?.dataset["cbWord"] ??
        undefined)
    : undefined;
}

/** A span of the row, with the class the stylesheet draws it by. */
function part(
  page: Document,
  className: string,
  text: string,
): HTMLSpanElement {
  const span = page.createElement("span");

  span.className = className;
  span.textContent = text;

  return span;
}

/** The reading, a syllable to a span, each carrying the tone it is read in. */
function reading(
  page: Document,
  syllables: readonly CidianSyllable[],
): HTMLSpanElement {
  const span = part(page, "cb-search-suggestion-reading", "");

  span.append(
    ...syllables.map((syllable) =>
      part(
        page,
        syllable.tone === untoned
          ? "py-syllable"
          : `py-syllable py-tone-${String(syllable.tone)}`,
        syllable.text,
      ),
    ),
  );

  return span;
}

/** The row for one suggestion, at its place in the list. */
export function suggestionOption(properties: {
  /** The document the list is in, which the elements belong to. */
  readonly page: Document;

  /** The word and the reading to draw. */
  readonly suggestion: CidianSuggestion;

  /** Its own id, which the box names while the cursor is on it. */
  readonly id: string;
}): HTMLLIElement {
  const { page, suggestion, id } = properties;
  const option = page.createElement("li");

  option.className = "cb-search-suggestion";
  option.id = id;
  option.setAttribute("role", "option");
  option.setAttribute("aria-selected", "false");
  option.dataset["cbWord"] = suggestion.word;
  option.append(
    part(page, "cb-search-suggestion-word", suggestion.word),
    reading(page, suggestion.syllables),
  );

  return option;
}
